import React, {
    Component,
    FC,
    forwardRef,
    ForwardRefRenderFunction,
    Fragment,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";

import PhoneEmail from "./PhoneEmail";
import Code from "./Code";
import PersonalDetails from "./PersonalDetails";
import Address from "./Address";
import ConfirmationData from "./ConfirmationData";
import Confirmation from "./Confirmation";
import ChangeDetails from "./ChangeDetails";
import ParentInfo from "./ParentInfo";
import Campus from "./Campus";
import Success from "./Success";
import Erro404 from "../404";

import { State, FormProps, Fields } from "./types";
import UpdateDataConfirmation from "./UpdateDataConfirmation";
import _ from "lodash";

import { Props } from "../propTypes";
import TshirtSize from "./TshirtSize";
import { ObjectLiteral } from "../../constants";
import { ReactCookieProps, useCookies, withCookies } from "react-cookie";
import { ResetForm } from "./ResetForm";

// Main Form Component
/**
 * We are using a switch statement for each of the steps in the form.
 * Each step contains a different component to handle the different information
 * we will be collecting from users.
 */

const getContent = (
    props: Props & FormProps,
    step: number,
    prevStep: number,
    values: Fields,
    nextStep: Function,
    phoneOrEmail: Function,
    handleChange: Function,
    isContactAllowedToUpdateSalesForceInfo: (
        userData: ObjectLiteral
    ) => boolean,
    changeDetails: Function,
    viewAction?: Function
) => {
    switch (step) {
        case 1:
        case 10:
            return (
                <PhoneEmail
                    nextStep={nextStep}
                    handleChange={handleChange}
                    phoneOrEmail={phoneOrEmail}
                    step={"email"}
                    values={values}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );
        case 2:
            return (
                <Code
                    nextStep={nextStep}
                    handleChange={handleChange}
                    phoneOrEmail={phoneOrEmail}
                    values={values}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );
        case 3:
        case 4:
            return (
                <PersonalDetails
                    nextStep={nextStep}
                    handleChange={handleChange}
                    values={values}
                    step={step === 4 ? "DOB" : "name-email"}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );

        case 5:
            return (
                <Address
                    nextStep={nextStep}
                    handleChange={handleChange}
                    values={values}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );
        case 6:
            // put extra steps here
            //step 9 = the edit data form we dont need to execute the extrasteps if comes from the edit form
            if (
                props.extraStep &&
                _.isFunction(props.extraStep) &&
                prevStep !== 9
            ) {
                return (
                    props.extraStep &&
                    props.extraStep(values, nextStep, handleChange)
                );
            }
            break;
        case 7:
            return (
                <Confirmation
                    nextStep={nextStep}
                    changeDetails={changeDetails}
                    handleChange={handleChange}
                    values={values}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );
        case 8:
            return (
                <Success
                    nextStep={nextStep}
                    values={values}
                    handleChange={handleChange}
                    {...props}
                />
            );
        case 9:
            return (
                <ChangeDetails
                    handleChange={handleChange}
                    changeDetails={changeDetails}
                    values={values}
                    nextStep={nextStep}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );

        case 11:
            return (
                <Campus
                    handleChange={handleChange}
                    changeDetails={changeDetails}
                    values={values}
                    nextStep={nextStep}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );
        case 12:
        case 13:
            return (
                <ParentInfo
                    handleChange={handleChange}
                    changeDetails={changeDetails}
                    values={values}
                    nextStep={nextStep}
                    viewAction={viewAction}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );
        case 14:
            return (
                <UpdateDataConfirmation
                    handleChange={handleChange}
                    changeDetails={changeDetails}
                    values={values}
                    nextStep={nextStep}
                    viewAction={viewAction}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );
        case 15:
            return (
                <TshirtSize
                    handleChange={handleChange}
                    changeDetails={changeDetails}
                    values={values}
                    nextStep={nextStep}
                    viewAction={viewAction}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    {...props}
                />
            );

        case 16:
            return (
                <ConfirmationData
                    nextStep={nextStep}
                    changeDetails={changeDetails}
                    handleChange={handleChange}
                    values={values}
                    isContactAllowedToUpdateSalesForceInfo={
                        isContactAllowedToUpdateSalesForceInfo
                    }
                    showConfirmationExtraFields={
                        props.showConfirmationExtraFields || undefined
                    }
                    {...props}
                />
            );
    }
};

export const ECFormFunc: ForwardRefRenderFunction<any, Props & FormProps> = (
    props,
    ref
) => {
    const [step, setStep] = useState(props.startingStep || 1);
    const [prevStep, setPrevStep] = useState(0);

    const [phone, setPhone] = useState("");
    const [phoneFormat, setPhoneFormat] = useState({});
    const [parentPhoneFormatData, setParentPhoneFormatData] = useState({});
    const [code, setCode] = useState("");
    const [code1, setCode1] = useState("");
    const [code2, setCode2] = useState("");
    const [code3, setCode3] = useState("");
    const [code4, setCode4] = useState("");
    const [code5, setCode5] = useState("");
    const [code6, setCode6] = useState("");

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [birthday, setBirthday] = useState("");

    const [country, setCountry] = useState("");
    const [countryName, setCountryName] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");

    const [zip, setZip] = useState("");
    const [parentFirstName, setParentFirstName] = useState("");
    const [parentLastName, setParentLastName] = useState("");
    const [parentEmail, setParentEmail] = useState("");
    const [parentPhone, setParentPhone] = useState("");

    const [input, setInput] = useState("");
    const [phoneConfirmation, setPhoneConfirmation] = useState("");
    const [phoneConfirmationFormat, setPhoneConfirmationFormat] = useState({});
    const [emailConfirmation, setEmailConfirmation] = useState("");

    const [tshirtSize, setTshirtSize] = useState("");

    const [campus, setCampus] = useState("");
    const [campusName, setCampusName] = useState("");

    const [fromSalesForce, setFromSalesForce] = useState(false);

    const [cookies, setCookie] = useCookies([
        "success_" + props.registrationType,
    ]);

    const setSuccessCookie = () => {
        setCookie("success_" + props.registrationType, "1", {
            maxAge: 60,
            path: "/",
        });
    };

    const setValues = {
        phone: setPhone,
        phoneFormat: setPhoneFormat,
        parentPhoneFormatData: setParentPhoneFormatData,
        code: setCode,
        code1: setCode1,
        code2: setCode2,
        code3: setCode3,
        code4: setCode4,
        code5: setCode5,
        code6: setCode6,
        firstName: setFirstName,
        lastName: setLastName,
        email: setEmail,
        birthday: setBirthday,

        country: setCountry,
        countryName: setCountryName,
        address: setAddress,
        city: setCity,
        state: setState,

        zip: setZip,
        parentFirstName: setParentFirstName,
        parentLastName: setParentLastName,
        parentEmail: setParentEmail,
        parentPhone: setParentPhone,

        input: setInput,
        phoneConfirmation: setPhoneConfirmation,
        phoneConfirmationFormat: setPhoneConfirmationFormat,
        emailConfirmation: setEmailConfirmation,
        tshirtSize: setTshirtSize,
        campus: setCampus,
        campusName: setCampusName,
        fromSalesForce: setFromSalesForce,
    };

    const values = {
        phone,
        phoneFormat,
        parentPhoneFormatData,
        code,
        code1,
        code2,
        code3,
        code4,
        code5,
        code6,
        firstName,
        lastName,
        email,
        birthday,
        country,
        countryName,
        address,
        city,
        state,
        zip,
        parentFirstName,
        parentLastName,
        parentEmail,
        parentPhone,
        input,
        phoneConfirmation,
        phoneConfirmationFormat,
        emailConfirmation,
        tshirtSize,
        campus,
        campusName,
        fromSalesForce,
        step,
    };

    const [viewAction, setViewAction] = useState(undefined);

    useEffect(() => {
        if (_.isFunction(props.afterStepUpdate)) {
            props.afterStepUpdate(step);
        }

        if (step === 8) {
            setSuccessCookie();
        }

        if (step === 6) {
            if (
                !_.isFunction(props.extraStep) ||
                (_.isFunction(props.extraStep) && prevStep === 9)
            ) {
                setStep(16);
            }
        }
    }, [step]);

    const nextStep = (
        nextStep: number | null = null,
        viewAction?: Function | undefined
    ) => {
        const newStep = nextStep || step + 1;

        setStep(newStep === 6 && step === 9 ? 16 : newStep);
        setPrevStep(step);
        setViewAction(viewAction);
    };

    const changeDetails = () => {
        if (step === 9) {
            nextStep(16);
        } else {
            nextStep(9);
        }
    };

    const phoneOrEmail = () => {
        if (step === 1 || step === 2) {
            nextStep(10);
        } else if (step === 10) {
            nextStep(1);
        } else {
            nextStep(4);
        }
    };

    const handleChange = (input: any) => (e: any, value?: any) => {
        if (value || value === "") {
            if (setValues[input]) setValues[input](value);
        } else if (e && e.target && (e.target.value || e.target.value === "")) {
            if (setValues[input]) setValues[input](e.target.value);
        }
    };

    const isContactAllowedToUpdateSalesForceInfo = (userData) => {
        if (
            props.isContactAllowedToUpdateSalesForceInfoCallback &&
            _.isFunction(props.isContactAllowedToUpdateSalesForceInfoCallback)
        ) {
            return props.isContactAllowedToUpdateSalesForceInfoCallback(
                userData
            );
        }

        return false;
    };

    useImperativeHandle(ref, () => ({
        nextStep,
        handleChange,
    }));

    if (props.showError404 || props.registrationType === null) {
        return (
            <Erro404 containerClass="flex flex-justify-center flex-align-center flex-column m-b-spacer-xl" />
        );
    }

    return (
        <>
            {getContent(
                props,
                step,
                prevStep,
                values,
                nextStep,
                phoneOrEmail,
                handleChange,
                isContactAllowedToUpdateSalesForceInfo,
                changeDetails,
                viewAction
            )}
        </>
    );
};

const ECForm = forwardRef(ECFormFunc);

export default ECForm;
